import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import companiesApi, { CompanyDTO } from '../services/companies.ts';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { applyCurrencyMask, parseCurrency, formatCurrencyInput } from '../utils/currency.ts';

const currencyToNumber = (v: string) =>
  Number(
    v
      .replace(/[^0-9,-]/g, '')
      .replace('.', '')
      .replace(',', '.')
  ) || 0;

// Máscara de CNPJ: 00.000.000/0000-00
const applyCnpjMask = (value: string): string => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 14);
  const padded = digits;
  if (padded.length <= 2) return padded;
  if (padded.length <= 5) return padded.replace(/(\d{2})(\d+)/, '$1.$2');
  if (padded.length <= 8) return padded.replace(/(\d{2})(\d{3})(\d+)/, '$1.$2.$3');
  if (padded.length <= 12) return padded.replace(/(\d{2})(\d{3})(\d{3})(\d+)/, '$1.$2.$3/$4');
  return padded.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const EditCompany: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CompanyDTO>({
    defaultValues: {
      name: '',
      cnpj: '',
      profitPercent: null,
      annualProfit: null,
      valuation: 0,
      annualRevenue: 0,
      annualExpenses: 0,
      status: 'active',
    },
  });

  // Estados de exibição com máscara
  const [cnpjDisplay, setCnpjDisplay] = useState('');
  const [revenueDisplay, setRevenueDisplay] = useState('');
  const [expensesDisplay, setExpensesDisplay] = useState('');
  const [valuationDisplay, setValuationDisplay] = useState('');

  // Cálculo automático do lucro baseado nos valores mascarados exibidos
  const revenueNumeric = parseCurrency(revenueDisplay);
  const expensesNumeric = parseCurrency(expensesDisplay);
  const profitValue = revenueNumeric - expensesNumeric;
  const profitPercentCalc = revenueNumeric > 0 ? (profitValue / revenueNumeric) * 100 : 0;

  const { data } = useQuery(['company', id], () => companiesApi.get(Number(id)), {
    enabled: isEdit,
  });

  useEffect(() => {
    if (data) {
      reset({
        name: data.name,
        cnpj: data.cnpj,
        profitPercent: data.profitPercent ?? null,
        annualProfit: data.annualProfit ?? null,
        valuation: data.valuation ?? null,
        annualRevenue: data.annualRevenue ?? null,
        annualExpenses: data.annualExpenses ?? null,
        status: data.status ?? 'active',
      });

      // Inicializa exibições mascaradas
      setCnpjDisplay(applyCnpjMask(data.cnpj || ''));
      setRevenueDisplay(
        data.annualRevenue != null ? formatCurrencyInput(Number(data.annualRevenue)) : ''
      );
      setExpensesDisplay(
        data.annualExpenses != null ? formatCurrencyInput(Number(data.annualExpenses)) : ''
      );
      setValuationDisplay(
        data.valuation != null ? formatCurrencyInput(Number(data.valuation)) : ''
      );
    }
  }, [data, reset]);

  const createMutation = useMutation((payload: CompanyDTO) => companiesApi.create(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(['companies']);
      navigate('/companies');
    },
  });

  const updateMutation = useMutation(
    (payload: CompanyDTO) => companiesApi.update(Number(id), payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['companies']);
        navigate('/companies');
      },
    }
  );

  const onSubmit = (form: CompanyDTO) => {
    // Deriva todos os campos numéricos a partir dos valores mascarados exibidos,
    // garantindo consistência e evitando null/NaN.
    const valuationNum = parseCurrency(valuationDisplay);
    const revenueNum = parseCurrency(revenueDisplay);
    const expensesNum = parseCurrency(expensesDisplay);

    const payload: CompanyDTO = {
      ...form,
      // Calculados automaticamente com base em receita e despesa
      profitPercent: Number(profitPercentCalc.toFixed(2)),
      annualProfit: Number(profitValue.toFixed(2)),
      // Demais valores numéricos sempre definidos (nunca null)
      valuation: Number.isFinite(valuationNum) ? valuationNum : 0,
      annualRevenue: Number.isFinite(revenueNum) ? revenueNum : 0,
      annualExpenses: Number.isFinite(expensesNum) ? expensesNum : 0,
    };

    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/companies')} className="p-2 rounded hover:bg-gray-100">
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-xl sm:text-2xl font-semibold">
            {isEdit ? 'Editar Empresa' : 'Nova Empresa'}
          </h1>
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white p-4 sm:p-6 rounded-md shadow-sm"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700">Nome</label>
          <input
            {...register('name', { required: 'Informe o nome' })}
            className="mt-1 w-full border rounded-md p-2"
          />
          {errors.name && <span className="text-red-600 text-sm">{errors.name.message}</span>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">CNPJ</label>
          <input
            {...register('cnpj', { required: 'Informe o CNPJ' })}
            value={cnpjDisplay}
            onChange={e => {
              const masked = applyCnpjMask(e.target.value);
              setCnpjDisplay(masked);
              const digits = masked.replace(/\D/g, '');
              setValue('cnpj', digits);
            }}
            className="mt-1 w-full border rounded-md p-2"
            placeholder="00.000.000/0000-00"
          />
          {errors.cnpj && <span className="text-red-600 text-sm">{errors.cnpj.message}</span>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Valuation (R$)</label>
          <input
            {...register('valuation', { required: 'Informe o valuation' })}
            value={valuationDisplay}
            onChange={e => {
              const masked = applyCurrencyMask(e.target.value);
              setValuationDisplay(masked);
              setValue('valuation', parseCurrency(masked));
            }}
            className="mt-1 w-full border rounded-md p-2"
            placeholder="0,00"
          />
          {errors.valuation && (
            <span className="text-red-600 text-sm">{errors.valuation.message}</span>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Receita Anual (R$)</label>
          <input
            {...register('annualRevenue')}
            value={revenueDisplay}
            onChange={e => {
              const masked = applyCurrencyMask(e.target.value);
              setRevenueDisplay(masked);
              setValue('annualRevenue', parseCurrency(masked));
            }}
            className="mt-1 w-full border rounded-md p-2"
            placeholder="0,00"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Despesas Anuais (R$)</label>
          <input
            {...register('annualExpenses')}
            value={expensesDisplay}
            onChange={e => {
              const masked = applyCurrencyMask(e.target.value);
              setExpensesDisplay(masked);
              setValue('annualExpenses', parseCurrency(masked));
            }}
            className="mt-1 w-full border rounded-md p-2"
            placeholder="0,00"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <select {...register('status')} className="mt-1 w-full border rounded-md p-2">
            <option value="active">Ativa</option>
            <option value="inactive">Inativa</option>
          </select>
        </div>

        {/* Campos calculados e somente leitura no fim */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Lucro Anual (R$)</label>
          <input
            value={
              Number.isFinite(profitValue) && profitValue !== 0
                ? formatCurrencyInput(profitValue)
                : ''
            }
            readOnly
            className="mt-1 w-full border rounded-md p-2 bg-gray-50"
            placeholder="0,00"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">% Lucro</label>
          <input
            value={profitPercentCalc ? profitPercentCalc.toFixed(2) : ''}
            readOnly
            className="mt-1 w-full border rounded-md p-2 bg-gray-50"
            placeholder="0,00"
          />
        </div>

        <div className="sm:col-span-2 flex justify-end mt-2">
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {isEdit ? 'Salvar Alterações' : 'Criar Empresa'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCompany;
